import { Feature } from "@/gql/gen";
import { useFeatureStore } from "@/modules/feature/store/featrue";
import { storeToRefs } from "pinia";
import { computed, MaybeRefOrGetter, toValue } from "vue";

/**
 * Feature с вычисленным статусом активности
 */
export interface FeatureWithActivity extends Feature {
    ACTIVE_COMPUTED: boolean;
}

/**
 * Composable для работы с Features товара
 * 
 * Принимает FEATURE_IDS товара и находит соответствующие Features в FeatureStore.
 * Товары уже содержат полные FEATURE_IDS (собственные + от родительских секций),
 * так как обогащение происходит в catalog store при загрузке.
 * 
 * @param featureIds - массив ID Features (может быть ref, computed или getter)
 * @returns объект с features (с вычисленной активностью) и вспомогательными computed
 * 
 * Requirements: 3.2, 3.4
 */
export function useProductFeatures(featureIds: MaybeRefOrGetter<number[] | null | undefined>) {
    const featureStore = useFeatureStore();
    const {features: allFeatures} = storeToRefs(featureStore);

    /**
     * Features товара с вычисленным статусом активности
     * Features уже содержат ACTIVE_COMPUTED из FeatureStore
     */
    const features = computed<FeatureWithActivity[]>(() => {
        const ids = toValue(featureIds);
        if (!ids || ids.length === 0) {
            return [];
        }

        // Находим Features по ID из store
        // allFeatures уже содержит ACTIVE_COMPUTED, вычисленный в FeatureStore
        const allFeaturesArray = allFeatures.value || [];
        return allFeaturesArray.filter(feature => 
            ids.includes(feature.ID)
        ) as FeatureWithActivity[];
    });

    /**
     * Только активные Features (по расписанию)
     */
    const activeFeatures = computed<FeatureWithActivity[]>(() => {
        return features.value.filter(feature => feature.ACTIVE_COMPUTED);
    });

    /**
     * Только неактивные Features (вне расписания)
     */
    const inactiveFeatures = computed<FeatureWithActivity[]>(() => {
        return features.value.filter(feature => !feature.ACTIVE_COMPUTED);
    });

    /**
     * Есть ли хотя бы одна Feature
     */
    const hasFeatures = computed(() => features.value.length > 0);

    /**
     * Все Features активны
     */
    const allFeaturesActive = computed(() => {
        if (features.value.length === 0) return true;
        return features.value.every(feature => feature.ACTIVE_COMPUTED);
    });

    /**
     * Хотя бы одна Feature неактивна
     */
    const hasInactiveFeatures = computed(() => inactiveFeatures.value.length > 0);

    return {
        features,
        activeFeatures,
        inactiveFeatures,
        hasFeatures,
        allFeaturesActive,
        hasInactiveFeatures
    };
}

export default useProductFeatures;
